/**
 * Outreach Approval Queue (plan.md Phase 6 - Outreach Command Center)
 *
 * Human-in-the-loop queue for prospect outreach: a draft (from the AI Sales
 * Toolkit or written by hand) is edited, submitted, approved or rejected by
 * a Sales Manager / Master Administrator, and only then sent. Backed by
 * backend/outreach_routes.py, which enforces every rule shown here (state
 * machine, roles, lead scoping, suppression, honest provider failures) -
 * the UI only hides buttons that would be refused anyway.
 *
 * Used standalone (staff nav "Outreach Approval") and embedded per lead in
 * the Lead Command Center's Sales Toolkit tab (pass `leadId`).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  AlertCircle, CheckCircle2, Clock, Mail, MessageSquare, RefreshCw, Send, ShieldAlert, XCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const APPROVER_ROLES = ['Master Administrator', 'Sales Manager'];
const EDITABLE = ['draft', 'pending_approval', 'rejected'];
const SENDABLE = ['approved', 'failed'];

const STATUS_META = {
  draft: { label: 'Draft', className: 'bg-white/10 text-white/80 border-white/20' },
  pending_approval: { label: 'Pending approval', className: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  approved: { label: 'Approved', className: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  rejected: { label: 'Rejected', className: 'bg-red-500/15 text-red-300 border-red-500/30' },
  sending: { label: 'Sending', className: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  sent: { label: 'Sent', className: 'bg-green-500/20 text-green-300 border-green-500/40' },
  failed: { label: 'Failed', className: 'bg-red-500/20 text-red-300 border-red-500/40' },
};
const FILTERS = ['all', 'pending_approval', 'draft', 'rejected', 'approved', 'failed', 'sent'];

const errorDetail = async (response, fallback) => {
  try {
    const data = await response.json();
    if (typeof data.detail === 'string') return data.detail;
  } catch (e) { /* not JSON */ }
  return fallback;
};

/** Copy one AI toolkit field (first_email, follow_up_email_1/2, sms_draft)
 * into a new outreach draft. The backend reads the text from the toolkit
 * stored on the lead; nothing is sent. */
export async function createDraftFromToolkit(user, leadId, toolkitKey) {
  const response = await fetch(`${API}/outreach/drafts/from-toolkit`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${user.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ lead_id: leadId, toolkit_key: toolkitKey }),
  });
  if (!response.ok) throw new Error(await errorDetail(response, 'Could not create the draft'));
  return response.json();
}

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, className: '' };
  return <Badge variant="outline" className={meta.className} data-testid={`outreach-status-${status}`}>{meta.label}</Badge>;
};

const ChannelIcon = ({ channel }) => (channel === 'sms' ? <MessageSquare className="h-4 w-4" /> : <Mail className="h-4 w-4" />);

const OutreachApprovalQueue = ({ user, leadId = null, title = 'Outreach Approval Queue', refreshKey = 0 }) => {
  const isApprover = APPROVER_ROLES.includes(user?.role);
  const [filter, setFilter] = useState(!leadId && isApprover ? 'pending_approval' : 'all');
  const [drafts, setDrafts] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ subject: '', body: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${user.token}`,
    'Content-Type': 'application/json',
  }), [user.token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('status', filter);
      if (leadId) params.set('lead_id', leadId);
      const response = await fetch(`${API}/outreach/drafts?${params.toString()}`, { headers: headers() });
      if (response.ok) {
        const data = await response.json();
        setDrafts(data.drafts || []);
        setCounts(data.counts || {});
      } else {
        toast.error(await errorDetail(response, 'Could not load outreach drafts'));
      }
    } catch (error) {
      toast.error('Could not load outreach drafts');
    } finally {
      setLoading(false);
    }
  }, [filter, leadId, headers]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const open = (draft) => {
    setSelected(draft);
    setForm({ subject: draft.subject || '', body: draft.body || '' });
    setRejectReason('');
  };

  const applyResult = (draft) => {
    setSelected(draft);
    setForm({ subject: draft.subject || '', body: draft.body || '' });
    load();
  };

  const act = async (path, { method = 'POST', body, success } = {}) => {
    if (!selected) return null;
    setBusy(true);
    try {
      const response = await fetch(`${API}/outreach/drafts/${selected.id}${path}`, {
        method,
        headers: headers(),
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!response.ok) {
        toast.error(await errorDetail(response, 'Action failed'));
        load();
        return null;
      }
      const data = await response.json();
      if (success) toast.success(success);
      return data;
    } catch (error) {
      toast.error('Action failed');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    const payload = { body: form.body };
    if (selected.channel === 'email') payload.subject = form.subject;
    const data = await act('', { method: 'PATCH', body: payload, success: 'Draft saved' });
    if (data) applyResult(data);
  };

  const submit = async () => {
    const dirty = form.body !== (selected.body || '') || (selected.channel === 'email' && form.subject !== (selected.subject || ''));
    if (dirty) {
      toast.error('Save your changes before submitting');
      return;
    }
    const data = await act('/submit', { success: 'Submitted for approval' });
    if (data) applyResult(data);
  };

  const approve = async () => {
    const data = await act('/approve', { body: { version: selected.version }, success: 'Approved - not sent yet' });
    if (data) applyResult(data);
  };

  const reject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Give the author a reason');
      return;
    }
    const data = await act('/reject', { body: { reason: rejectReason.trim(), version: selected.version }, success: 'Rejected' });
    if (data) { setRejectReason(''); applyResult(data); }
  };

  const send = async () => {
    const where = selected.compliance?.recipient || 'the lead';
    if (!window.confirm(`Send this approved ${selected.channel === 'sms' ? 'SMS' : 'email'} to ${where} now?`)) return;
    const data = await act('/send');
    if (data) {
      if (data.sent) toast.success('Sent');
      else toast.error(data.draft?.failure_reason || 'Not sent');
      applyResult(data.draft);
    }
  };

  const createManual = async (channel) => {
    setBusy(true);
    try {
      const response = await fetch(`${API}/outreach/drafts`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ lead_id: leadId, channel, subject: '', body: '' }),
      });
      if (!response.ok) {
        toast.error(await errorDetail(response, 'Could not create the draft'));
        return;
      }
      const draft = await response.json();
      open(draft);
      load();
    } catch (error) {
      toast.error('Could not create the draft');
    } finally {
      setBusy(false);
    }
  };

  const isAuthor = selected && selected.created_by_id === user.id;
  const canEdit = selected && EDITABLE.includes(selected.status) && (isApprover || isAuthor);
  const canSubmit = selected && ['draft', 'rejected'].includes(selected.status) && (isApprover || isAuthor);
  const canDecide = selected && isApprover && selected.status === 'pending_approval';
  const canSend = selected && SENDABLE.includes(selected.status) && (isApprover || isAuthor);
  const suppressed = selected?.compliance?.suppressed;

  return (
    <div className="space-y-4" data-testid="outreach-approval-queue">
      <Card className="glass-panel border-white/10">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-white/90">{title}</CardTitle>
              <p className="text-xs text-white/60 mt-1">
                Nothing reaches a prospect without approval by a Sales Manager or the Master Administrator. Opted-out contacts are blocked at approval and again at send.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {leadId && (
                <>
                  <Button variant="outline" className="border-white/20 text-white/80" onClick={() => createManual('email')} disabled={busy} data-testid="outreach-new-email-btn">
                    <Mail className="h-4 w-4" /> New email draft
                  </Button>
                  <Button variant="outline" className="border-white/20 text-white/80" onClick={() => createManual('sms')} disabled={busy} data-testid="outreach-new-sms-btn">
                    <MessageSquare className="h-4 w-4" /> New SMS draft
                  </Button>
                </>
              )}
              <Button variant="outline" className="border-white/20 text-white/80" onClick={load} disabled={loading} data-testid="outreach-refresh-btn">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {FILTERS.map((key) => {
              const count = key === 'all' ? Object.values(counts).reduce((a, b) => a + b, 0) : counts[key] || 0;
              return (
                <Button
                  key={key}
                  size="sm"
                  variant={filter === key ? 'default' : 'outline'}
                  className={filter === key ? 'primary-btn' : 'border-white/20 text-white/70'}
                  onClick={() => setFilter(key)}
                  data-testid={`outreach-filter-${key}`}
                >
                  {key === 'all' ? 'All' : STATUS_META[key].label} ({count})
                </Button>
              );
            })}
          </div>
        </CardHeader>
        <CardContent>
          {drafts.length === 0 ? (
            <p className="text-sm text-white/50 py-6 text-center">{loading ? 'Loading…' : 'No outreach drafts here.'}</p>
          ) : (
            <div className="space-y-2">
              {drafts.map((draft) => (
                <button
                  type="button"
                  key={draft.id}
                  onClick={() => open(draft)}
                  className="w-full text-left p-3 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition"
                  data-testid={`outreach-draft-${draft.id}`}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <ChannelIcon channel={draft.channel} />
                    <strong className="text-white/90 text-sm">{draft.clinic_name || draft.lead_id}</strong>
                    <StatusBadge status={draft.status} />
                    {draft.compliance?.suppressed && (
                      <Badge variant="outline" className="bg-red-500/15 text-red-300 border-red-500/30"><ShieldAlert className="h-3 w-3 mr-1" />Opted out</Badge>
                    )}
                    {!draft.compliance?.has_recipient && (
                      <Badge variant="outline" className="bg-orange-500/15 text-orange-300 border-orange-500/30">No {draft.channel === 'sms' ? 'phone' : 'email'}</Badge>
                    )}
                    <span className="text-xs text-white/40 ml-auto">v{draft.version} · {draft.created_by} · {new Date(draft.updated_at).toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-white/60 mt-1 truncate">
                    {draft.channel === 'email' && draft.subject ? `${draft.subject} — ` : ''}{draft.body}
                  </p>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(selected)} onOpenChange={(isOpen) => { if (!isOpen) setSelected(null); }}>
        {selected && (
          <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="outreach-draft-dialog">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <ChannelIcon channel={selected.channel} />
                {selected.channel === 'sms' ? 'SMS' : 'Email'} to {selected.clinic_name || selected.lead_id}
                <StatusBadge status={selected.status} />
              </DialogTitle>
              <DialogDescription className="text-white/60">
                Recipient: {selected.compliance?.recipient || 'none on file'} · version {selected.version} · source {selected.source}
              </DialogDescription>
            </DialogHeader>

            {suppressed && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex gap-2">
                <ShieldAlert className="h-4 w-4 mt-0.5" /> This recipient has opted out. It can't be approved or sent.
              </div>
            )}
            {selected.status === 'rejected' && selected.rejection_reason && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
                Rejected by {selected.rejected_by}: {selected.rejection_reason}
              </div>
            )}
            {selected.status === 'failed' && selected.failure_reason && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex gap-2" data-testid="outreach-failure-reason">
                <AlertCircle className="h-4 w-4 mt-0.5" /> Not sent: {selected.failure_reason}
              </div>
            )}
            {selected.status === 'sent' && (
              <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-300 text-sm flex gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5" /> Sent {new Date(selected.sent_at).toLocaleString()} via {selected.provider} (approved by {selected.approved_by}).
              </div>
            )}

            <div className="space-y-3">
              {selected.channel === 'email' && (
                <div>
                  <label className="text-xs text-white/60">Subject</label>
                  <Input
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    disabled={!canEdit || busy}
                    className="bg-white/5 border-white/10 text-white mt-1"
                    data-testid="outreach-subject-input"
                  />
                </div>
              )}
              <div>
                <label className="text-xs text-white/60">Message</label>
                <Textarea
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  disabled={!canEdit || busy}
                  rows={10}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  data-testid="outreach-body-input"
                />
                <p className="text-xs text-white/40 mt-1">
                  {selected.channel === 'sms'
                    ? `${form.body.length}/1600 characters. "Reply STOP to opt out." is appended when sent unless the message already mentions STOP.`
                    : 'An unsubscribe link is added to the footer when sent.'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {canEdit && (
                <Button variant="outline" className="border-white/20 text-white/80" onClick={save} disabled={busy} data-testid="outreach-save-btn">Save changes</Button>
              )}
              {canSubmit && (
                <Button className="primary-btn" onClick={submit} disabled={busy} data-testid="outreach-submit-btn"><Clock className="h-4 w-4" /> Submit for approval</Button>
              )}
              {canDecide && (
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={approve} disabled={busy || suppressed} data-testid="outreach-approve-btn">
                  <CheckCircle2 className="h-4 w-4" /> Approve v{selected.version}
                </Button>
              )}
              {canSend && (
                <Button className="primary-btn" onClick={send} disabled={busy || suppressed} data-testid="outreach-send-btn">
                  <Send className="h-4 w-4" /> {selected.status === 'failed' ? 'Retry send' : 'Send now'}
                </Button>
              )}
            </div>

            {canDecide && (
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="text-xs text-white/60">Rejection reason (shown to the author)</label>
                  <Input
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="bg-white/5 border-white/10 text-white mt-1"
                    data-testid="outreach-reject-reason"
                  />
                </div>
                <Button variant="outline" className="border-red-500/40 text-red-300" onClick={reject} disabled={busy} data-testid="outreach-reject-btn">
                  <XCircle className="h-4 w-4" /> Reject
                </Button>
              </div>
            )}

            <div>
              <h4 className="text-sm font-semibold text-white/80 mb-2">Audit trail</h4>
              <ul className="space-y-1 text-xs text-white/60" data-testid="outreach-history">
                {[...(selected.history || [])].reverse().map((entry) => (
                  <li key={entry.id}>
                    <span className="text-white/40">{new Date(entry.at).toLocaleString()}</span>{' '}
                    <strong className="text-white/80">{entry.action.replace(/_/g, ' ')}</strong> by {entry.by_name} ({entry.by_role})
                    {entry.to_status && entry.from_status !== entry.to_status ? ` → ${entry.to_status.replace(/_/g, ' ')}` : ''}
                    {entry.self_approved ? ' · self-approved' : ''}
                    {entry.reason ? ` · ${entry.reason}` : ''}
                    {entry.changes ? ` · edited ${Object.keys(entry.changes).join(', ')} (v${entry.version})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
};

export default OutreachApprovalQueue;
